import type { NotificationChannelName } from '@realadvisor/constants';

import {
  type GetUserNotificationSettingsQuery,
  type User_Notification_Preferences_Insert_Input,
  User_Notification_Preferences_Update_Column,
} from '@graphql/__generated__/graphql';
import type { Translate } from '@shared/hooks/locale';

export type NotificationSetting =
  GetUserNotificationSettingsQuery['notification_settings'][number];

type NotificationPreference = NotificationSetting['user_preferences'][number];

export type NotificationChannelKey = NotificationChannelName | 'team_leader_cc';

type ChannelAvailability = Pick<
  NotificationSetting,
  `${NotificationChannelKey}_available`
>;

type ChannelOffer = ChannelAvailability &
  Pick<NotificationSetting, 'is_active'>;

type PreferenceValues = Pick<
  User_Notification_Preferences_Insert_Input,
  `${NotificationChannelKey}_enabled`
>;

export type NotificationChannel<
  Name extends NotificationChannelKey = NotificationChannelKey,
> = {
  name: Name;
  column: User_Notification_Preferences_Update_Column;
  isAvailable: (setting: ChannelAvailability) => boolean;
  getDefault: (setting: NotificationSetting) => boolean;
  getPreference: (
    preference: NotificationPreference | undefined,
  ) => boolean | null;
  toValues: (value: boolean) => PreferenceValues;
};

export const deliveryChannelsByName: Record<
  NotificationChannelName,
  NotificationChannel<NotificationChannelName>
> = {
  email: {
    name: 'email',
    column: User_Notification_Preferences_Update_Column.EmailEnabled,
    isAvailable: setting => setting.email_available,
    getDefault: setting => setting.email_default,
    getPreference: preference => preference?.email_enabled ?? null,
    toValues: value => ({ email_enabled: value }),
  },
  sms: {
    name: 'sms',
    column: User_Notification_Preferences_Update_Column.SmsEnabled,
    isAvailable: setting => setting.sms_available,
    getDefault: setting => setting.sms_default,
    getPreference: preference => preference?.sms_enabled ?? null,
    toValues: value => ({ sms_enabled: value }),
  },
  push: {
    name: 'push',
    column: User_Notification_Preferences_Update_Column.PushEnabled,
    isAvailable: setting => setting.push_available,
    getDefault: setting => setting.push_default,
    getPreference: preference => preference?.push_enabled ?? null,
    toValues: value => ({ push_enabled: value }),
  },
};

export const teamLeaderCcChannel: NotificationChannel<'team_leader_cc'> = {
  name: 'team_leader_cc',
  column: User_Notification_Preferences_Update_Column.TeamLeaderCcEnabled,
  isAvailable: setting => setting.team_leader_cc_available,
  getDefault: setting => setting.team_leader_cc_default,
  getPreference: preference => preference?.team_leader_cc_enabled ?? null,
  toValues: value => ({ team_leader_cc_enabled: value }),
};

const allChannels: NotificationChannel[] = [
  ...Object.values(deliveryChannelsByName),
  teamLeaderCcChannel,
];

export type NotificationChannelOption<
  Name extends NotificationChannelKey = NotificationChannelKey,
> = {
  channel: NotificationChannel<Name>;
  label: string;
};

export const getNotificationChannelOptions = (
  t: Translate,
): NotificationChannelOption<NotificationChannelName>[] => [
  { channel: deliveryChannelsByName.email, label: t('Email') },
  { channel: deliveryChannelsByName.sms, label: t('SMS') },
  { channel: deliveryChannelsByName.push, label: t('Push') },
];

export const getTeamLeaderCcOption = (
  t: Translate,
): NotificationChannelOption<'team_leader_cc'> => ({
  channel: teamLeaderCcChannel,
  label: t('Team leader copy'),
});

export const isChannelOffered = (
  setting: ChannelOffer,
  channel: NotificationChannel,
): boolean => setting.is_active && channel.isAvailable(setting);

export const isAnyChannelOffered = (setting: ChannelOffer): boolean =>
  allChannels.some(channel => isChannelOffered(setting, channel));

export const getShownChannelValue = (
  setting: NotificationSetting,
  channel: NotificationChannel,
): boolean =>
  channel.getPreference(setting.user_preferences[0]) ??
  channel.getDefault(setting);

// True when the user gets this notification on at least one offered channel.
const deliveryChannels = Object.values(deliveryChannelsByName);

export type UnusableChannels = ReadonlySet<NotificationChannelKey>;

const noUnusableChannels: UnusableChannels = new Set();

export const isRequiredChannelLocked = (
  setting: NotificationSetting,
  channel: NotificationChannel,
  unusable: UnusableChannels = noUnusableChannels,
): boolean =>
  setting.requires_channel &&
  channel.name !== 'team_leader_cc' &&
  !unusable.has(channel.name) &&
  isChannelOffered(setting, channel) &&
  getShownChannelValue(setting, channel) &&
  !deliveryChannels.some(
    other =>
      other.name !== channel.name &&
      !unusable.has(other.name) &&
      isChannelOffered(setting, other) &&
      getShownChannelValue(setting, other),
  );

export const REQUIRED_CHANNEL_ERROR =
  'At least one notification channel must remain active.';

export const assertRequiredChannelsStayOn = (
  settings: readonly NotificationSetting[],
  channel: NotificationChannel,
  value: boolean,
  unusable: UnusableChannels = noUnusableChannels,
): void => {
  if (
    !value &&
    settings.some(setting =>
      isRequiredChannelLocked(setting, channel, unusable),
    )
  ) {
    throw new Error(REQUIRED_CHANNEL_ERROR);
  }
};

export const isAnyChannelOn = (setting: NotificationSetting): boolean =>
  allChannels.some(
    channel =>
      isChannelOffered(setting, channel) &&
      getShownChannelValue(setting, channel),
  );
